<template>
  <v-card>
    <v-card-title>
      <platforms-logo :platforms="['reviewforest']"></platforms-logo>
      <span class="ml-2">RF Direct Reviews</span>
    </v-card-title>
    <v-card-subtitle>
      <v-row>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchByEmail"
            append-icon="search"
            label="Search by email"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="searchByCompanyName"
            append-icon="search"
            label="Search by company"
            single-line
            hide-details
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedProject"
            :items="projects"
            item-text="name"
            item-value="id"
            label="Select project"
            clearable
            @change="debounceFetching"
          ></v-select>
        </v-col>
        <v-col cols="12" md="3">
          <v-select
            v-model="selectedConfirmation"
            :items="confirmationTypes"
            label="Verified & Planted"
            clearable
            @change="debounceFetching"
          ></v-select>
        </v-col>
      </v-row>
    </v-card-subtitle>
    <v-spacer></v-spacer>
    <v-data-table
      :headers="headers"
      :items="treeReports"
      :loading="loading"
      :options.sync="options"
      :server-items-length="totalAmount"
      :footer-props="{
        'items-per-page-options': $options.DEFAULT_ITEMS_PER_PAGE_OPTIONS,
      }"
    >
      <template v-slot:[`item.platformReview`]="{ item }">
        <span
          style="cursor: pointer; text-decoration: underline"
          v-if="item.platformReview && item.platformReview.email"
          @click="showFullReview(item.platformReview)"
        >
          {{ item.platformReview.email }}
        </span>
      </template>

      <template v-slot:[`item.email`]="{ item }">
        <span v-if="item.email">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                style="cursor: pointer; text-decoration: underline"
              >
                {{
                  statuses.find((status) => status.id === item.email.status)
                    .name
                }}
              </span>
            </template>
            <span>
              <ul>
                <li v-for="sgLog in item.email.sgLogs" :key="sgLog.sg_event_id">
                  {{ sgLog.event }} - {{ formatDate(sgLog.timestamp * 1000) }}
                </li>
              </ul>
            </span>
          </v-tooltip>
        </span>
      </template>
      <template v-slot:[`item.ownedBy`]="{ item }">
        <v-menu
          v-model="item.showCompanySelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span v-if="item.ownedBy.name && item.ownedBy.name.length > 30">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.ownedBy.name) }}</span>
                  </template>
                  <span>{{ item.ownedBy.name }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.ownedBy.name }}
              </span>
            </span>
          </template>
          <v-divider></v-divider>
          <v-list dense>
            <v-list-item
              :to="{ name: 'Company', params: { id: item.ownedBy.id } }"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Open Company
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyCompanyEntity(item, 'Company ID', item.ownedBy.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Company ID
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="
                copyCompanyEntity(item, 'Company Name', item.ownedBy.name)
              "
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Company Name
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.forest`]="{ item }">
        <v-menu
          v-model="item.showForestSelector"
          :close-on-content-click="false"
          offset-y
        >
          <template v-slot:activator="{ on }">
            <span v-on="on" style="cursor: pointer; text-decoration: underline">
              <span v-if="item.forest.name && item.forest.name.length > 30">
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ truncateText(item.forest.name) }}</span>
                  </template>
                  <span>{{ item.forest.name }}</span>
                </v-tooltip>
              </span>
              <span v-else>
                {{ item.forest.name }}
              </span>
            </span>
          </template>
          <v-list dense>
            <v-list-item style="cursor: pointer">
              <v-list-item-title class="font-weight-medium">
                <a
                  :href="`${$options.FOREST_DOMAIN}/${item.forest.slug}`"
                  target="_blank"
                  style="text-decoration: none"
                >
                  Go to Forest Page 🔗
                </a>
                <v-icon>mdi-open-in-new</v-icon>
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              :to="{ name: 'Forest', params: { id: item.forest.id } }"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Open Forest
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest ID', item.forest.id)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest ID
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest Name', item.forest.name)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Name
              </v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item
              @click="copyForestEntity(item, 'Forest Slug', item.forest.slug)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Forest Slug
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="copyForestEntity(item, 'Platform ID', item.platform)"
              style="cursor: pointer"
            >
              <v-list-item-title class="font-weight-medium">
                Copy Platform ID
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:[`item.projectId`]="{ item }">
        {{
          PROJECT_TYPES.find((p) => Number(p.id) === Number(item.projectId))
            .name
        }}
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        {{ formatDate(item.createdAt) }}
      </template>
      <template v-slot:[`item.countingTimestamp`]="{ item }">
        <span v-if="item.confirmed">
          <v-menu
            v-model="item.showPlantingDateSelector"
            :close-on-content-click="false"
            offset-y
          >
            <template v-slot:activator="{ on }">
              <span
                v-on="on"
                style="cursor: pointer; text-decoration: underline"
              >
                {{ formatDate(item.countingTimestamp * 1000) }}
              </span>
            </template>
            <v-divider></v-divider>
            <v-list dense>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Planting Timestamp',
                    item.countingTimestamp
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Planting Timestamp
                </v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Stripe Subscription ID',
                    item.stripeSubscriptionId
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Stripe Subscription Id
                </v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item
                @click="
                  copyPlantingDateEntity(
                    item,
                    'Stripe Subscription Item ID',
                    item.stripeSubscriptionItemId
                  )
                "
                style="cursor: pointer"
              >
                <v-list-item-title class="font-weight-medium">
                  Copy Stripe Subscription Item ID
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </span>
      </template>
      <template v-slot:[`item.confirmed`]="{ item }">
        <span v-if="item.confirmed">
          <img
            svg-inline
            src="../../assets/images/icons/filled-success.svg"
            alt=""
          />
        </span>
        <span v-else>
          <img
            svg-inline
            src="../../assets/images/icons/canceling.svg"
            alt=""
          />
        </span>
      </template>
    </v-data-table>

    <v-dialog v-model="dialogFullReview" max-width="500">
      <v-card>
        <v-card-title></v-card-title>
        <v-spacer></v-spacer>
        <v-card-text>
          <span>
            Reviewer Email:
            <span
              style="cursor: pointer; text-decoration: underline"
              @click="copyToClipboard('Email', selectedReview.email, 'Email')"
            >
              <strong>{{ selectedReview.email }}</strong>
            </span>
          </span>
          <br />
          <span v-if="selectedReview.name">
            Reviewer Name: <strong>{{ selectedReview.name }}</strong>
          </span>
          <br />
          <span>
            Unique Id:
            <span
              style="cursor: pointer; text-decoration: underline"
              @click="
                copyToClipboard(
                  'Unique ID',
                  selectedReview.uniqueId,
                  'Unique ID'
                )
              "
            >
              <strong>{{ selectedReview.uniqueId }}</strong>
            </span>
          </span>
          <br />
          <span v-if="selectedReview.score">
            Score: <strong>{{ selectedReview.score }}</strong>
          </span>
          <br />
          <span v-if="selectedReview.date">
            Submitted At: <strong>{{ formatDate(selectedReview.date) }}</strong>
          </span>
          <br />
          <br />
          <i>"{{ selectedReview.text }}"</i>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogFullReview = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success message snackbar -->
    <success-snackbar v-model="showSuccessMessage" :message="successMessage" />
  </v-card>
</template>

<script>
import { API } from "../../api";
import { formatDate, truncateText } from "../../helpers";
import {
  PLATFORMS,
  PROJECT_TYPES,
  PLATFORM_TYPES,
  SENDGRID_EMAIL_STATUS,
  TREE_TYPES,
} from "../../constants";
import { writeToClipboard } from "../../utils/copyText";
import SuccessSnackbar from "../Base/SuccessSnackbar.vue";
import PlatformsLogo from "../Base/PlatformsLogo.vue";

export default {
  name: "TreeReportsReviewDirect",
  FOREST_DOMAIN: process.env.VUE_APP_FOREST_DOMAIN,
  components: {
    PlatformsLogo,
    SuccessSnackbar,
  },
  data() {
    return {
      formatDate,
      truncateText,
      PLATFORM_TYPES,
      PROJECT_TYPES,
      TREE_TYPES,
      treeReports: [],
      totalAmount: 0,
      loading: false,
      showSuccessMessage: false,
      successMessage: "",
      copiedKey: null,
      resetTimer: null,
      dialogFullReview: false,
      selectedReview: {
        name: "",
        text: "",
        email: "",
        uniqueId: "",
      },
      statuses: SENDGRID_EMAIL_STATUS,
      searchByEmail: "",
      searchByCompanyName: "",
      headers: [
        {
          text: "Submitted At",
          value: "createdAt",
          width: 200,
        },
        {
          text: "Reviewer Email",
          value: "platformReview",
          width: 200,
          sortable: false,
        },
        {
          text: "Company",
          value: "ownedBy",
          width: 150,
          sortable: false,
        },
        {
          text: "Forest",
          value: "forest",
          width: 100,
          sortable: false,
        },
        {
          text: "Project",
          value: "projectId",
          width: 100,
          sortable: false,
        },
        {
          text: "Planted At",
          value: "countingTimestamp",
          width: 200,
        },
        {
          text: "Verified & Planted",
          value: "confirmed",
          width: 50,
          sortable: false,
        },
        {
          text: "Email Verification",
          value: "email",
          width: 50,
          sortable: false,
        },
      ],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
        previousPage: 0,
      },
      selectedTreeType: null,
      treeTypes: TREE_TYPES,
      selectedPlatformType: PLATFORMS.REVIEWFOREST,
      platformTypes: PLATFORM_TYPES,
      confirmationTypes: [true, false],
      selectedProject: null,
      projects: PROJECT_TYPES,
      selectedConfirmation: null,
    };
  },
  created() {
    this.getRFDirectTrees();
  },
  computed: {
    searchedEmail() {
      const trimmedSearch = this.searchByEmail.trim();
      return trimmedSearch === "" ? null : trimmedSearch;
    },
    searchedByCompanyName() {
      const trimmedSearch = this.searchByCompanyName.trim();
      return trimmedSearch === "" ? null : trimmedSearch;
    },
  },
  watch: {
    searchByEmail: {
      handler: "debounceFetching",
    },
    searchByCompanyName: {
      handler: "debounceFetching",
    },
    options: {
      handler: "debounceFetching",
      deep: true,
    },
  },
  methods: {
    debounceFetching() {
      if (this.timerId) {
        clearTimeout(this.timerId);
      }

      this.timerId = setTimeout(() => {
        this.getRFDirectTrees();
      }, 500);
    },
    async getRFDirectTrees() {
      this.loading = true;
      try {
        const getPlantingReportForTable = await API.getPlantingReports({
          confirmed: this.selectedConfirmation,
          email: this.searchedEmail,
          companyName: this.searchedByCompanyName,
          sortBy: this.options.sortBy[0],
          order: this.options.sortDesc[0] ? "desc" : "asc",
          page: this.options.page,
          pageSize:
            this.options.itemsPerPage === -1 ? 0 : this.options.itemsPerPage,
          treeType: this.selectedTreeType,
          platformType: this.selectedPlatformType,
          projectId: this.selectedProject,
        });

        this.treeReports = getPlantingReportForTable.data;
        this.totalAmount = getPlantingReportForTable.count;

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    async copyToClipboard(label, value, key) {
      try {
        await writeToClipboard(String(value));
        this.copiedKey = key;
        // Show success message
        this.successMessage = `${label} successfully copied to clipboard!`;
        this.showSuccessMessage = true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to copy " + label, e);
      } finally {
        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => (this.copiedKey = null), 1500);
      }
    },
    copyForestEntity(item, type, value) {
      item.showForestSelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyCompanyEntity(item, type, value) {
      item.showCompanySelector = false;
      this.copyToClipboard(type, value, type);
    },
    copyPlantingDateEntity(item, type, value) {
      item.showPlantingDateSelector = false;
      this.copyToClipboard(type, value, type);
    },
    showFullReview(item) {
      this.selectedReview = item;
      this.dialogFullReview = true;
    },
  },
};
</script>
